import React from 'react';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import useGoogleLogin from './auth/useGoogleLogin';
import useMicrosoftLogin from './auth/useMicrosoftLogin';
import { handleImportError } from '../../person/ContactsImport/utils/ImportErrorHandler';

export const IMPORT_DATA_SOURCE_TYPES = {
    GOOGLE: 'GOOGLE',
    MICROSOFT: 'MICROSOFT',
    CSV_OR_EXCEL: 'CSV_OR_EXCEL',
    EMAIL_EML: 'EMAIL_EML',
    AI: 'AI',
    EXISTING_IMPORT: 'EXISTING_IMPORT'
} as const;

const GOOGLE_CONTACTS_SCOPE = 'https://www.googleapis.com/auth/contacts.readonly';
const MICROSOFT_CONTACTS_SCOPES = ['User.Read', 'Contacts.Read'];
const GOOGLE_CALENDERS_SCOPE =
    'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events.readonly https://www.googleapis.com/auth/calendar.calendarlist.readonly';

export type AuthOption = {
    dataSourceName: keyof typeof IMPORT_DATA_SOURCE_TYPES;
    iconComponent: React.ElementType;
    label: React.ReactNode;
    handleLogin?: () => void;
    disabled?: boolean;
    id?: string;
};

type DataSourceOptions = {
    google?: AuthOption;
    microsoft?: AuthOption;
    CSV?: AuthOption;
};

type AllAuthOptions = {
    contacts: DataSourceOptions;
    calenders: DataSourceOptions;
};

type GoogleLoginHandler = (scopes: string) => () => void;
type MicrosoftLoginHandler = (scopes: string[]) => () => Promise<void>;

interface UseAuthOptionsProps {
    setAccessToken: React.Dispatch<React.SetStateAction<string>>;
    setSelectedSource: React.Dispatch<React.SetStateAction<keyof typeof IMPORT_DATA_SOURCE_TYPES | null>>;
}

export default ({ setAccessToken, setSelectedSource }: UseAuthOptionsProps): AllAuthOptions => {
    const createGoogleLogin: GoogleLoginHandler = useGoogleLogin(
        (accessToken) => {
            setAccessToken(accessToken);
        },
        (error) => handleImportError(error, setSelectedSource),
        (error) => handleImportError(error, setSelectedSource)
    );

    const createMicrosoftLogin: MicrosoftLoginHandler = useMicrosoftLogin(
        (accessToken) => {
            setAccessToken(accessToken);
        },
        (error) => handleImportError(error, setSelectedSource)
    );

    const handleGoogleContactsLogin = createGoogleLogin(GOOGLE_CONTACTS_SCOPE);

    const handleMicrosoftContactsLogin = createMicrosoftLogin(MICROSOFT_CONTACTS_SCOPES);

    const handleGoogleCalendersLogin = createGoogleLogin(GOOGLE_CALENDERS_SCOPE);

    const allAuthOptions: AllAuthOptions = {
        contacts: {
            google: {
                dataSourceName: IMPORT_DATA_SOURCE_TYPES.GOOGLE,
                iconComponent: Icons.GoogleLogo,
                label: <I18n token="person.import.contacts.button.google" />,
                handleLogin: handleGoogleContactsLogin
            },
            microsoft: {
                dataSourceName: IMPORT_DATA_SOURCE_TYPES.MICROSOFT,
                iconComponent: Icons.MicrosoftLogo,
                label: <I18n token="person.import.contacts.button.microsoft" />,
                handleLogin: handleMicrosoftContactsLogin
            },
            CSV: {
                dataSourceName: IMPORT_DATA_SOURCE_TYPES.CSV_OR_EXCEL,
                iconComponent: Icons.ImportFile,
                label: <I18n token="person.import.contacts.button.csvorexel" />
            }
        },
        calenders: {
            google: {
                dataSourceName: IMPORT_DATA_SOURCE_TYPES.GOOGLE,
                iconComponent: Icons.GoogleLogo,
                label: <I18n token="personal.timeline.button.google" />,
                handleLogin: handleGoogleCalendersLogin
            }
        }
    };

    return allAuthOptions;
};
